import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Card, Row, Col, Button, Tag, Space, Modal, Form, Input, Select, Table, App as AntdApp, Tooltip, Popconfirm, Switch, Spin } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlayCircleOutlined, StopOutlined, DesktopOutlined, FundOutlined, DatabaseOutlined, ThunderboltOutlined, FileTextOutlined, CloudServerOutlined, SearchOutlined, WarningOutlined, CloseCircleOutlined, InfoCircleOutlined, FilterOutlined, DashboardOutlined, LineChartOutlined } from '@ant-design/icons';
import { agentsAPI, targetsAPI } from '../services/api';
import socketService from '../services/socket';
import AgentResourceStats from '../components/AgentResourceStats';
import AgentResourceChart from '../components/AgentResourceChart';
import TargetStats from '../components/TargetStats';
import LatencyChart from '../components/LatencyChart';
import { buildHardwareSummary, isNetworkUp, isNetworkDown } from '../utils/hardwareSummary';
import './Dashboard.css';

const { TextArea } = Input;

const AgentDetail = ({ agentId: propAgentId, embedded = false }) => {
  const params = useParams();
  const agentId = propAgentId || params.agentId;
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [monitoredItems, setMonitoredItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemModalMode, setItemModalMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('items');
  const [commandModalVisible, setCommandModalVisible] = useState(false);
  const [selectedItemForCommand, setSelectedItemForCommand] = useState(null);
  const [commandForm] = Form.useForm();
  const { message: antMessage } = AntdApp.useApp();

  // Process Explorer state
  const [processList, setProcessList] = useState([]);
  const [processListLoading, setProcessListLoading] = useState(false);
  const [processSortBy, setProcessSortBy] = useState('memory');
  const [processSearch, setProcessSearch] = useState('');

  // PM2 state
  const [pm2Processes, setPm2Processes] = useState([]);
  const [pm2Loading, setPm2Loading] = useState(false);

  // Hardware state
  const [hardwareData, setHardwareData] = useState(null);
  const [hardwareLoading, setHardwareLoading] = useState(false);
  const hardwareSummary = useMemo(
    () => (hardwareData ? buildHardwareSummary(hardwareData) : null),
    [hardwareData]
  );

  // Event Viewer state
  const [eventLogs, setEventLogs] = useState([]);
  const [eventLogsLoading, setEventLogsLoading] = useState(false);
  const [evLogName, setEvLogName] = useState('Application');
  const [evLevel, setEvLevel] = useState(null);
  const [evHours, setEvHours] = useState(24);
  const [evMaxEvents] = useState(50);
  const [evKeyword, setEvKeyword] = useState('');

  const [targetStatsVisible, setTargetStatsVisible] = useState(false);
  const [selectedTargetForStats, setSelectedTargetForStats] = useState(null);
  const [targetLogs, setTargetLogs] = useState([]);
  const [targetStatsLoading, setTargetStatsLoading] = useState(false);
  const [targetLogsLoading, setTargetLogsLoading] = useState(false);
  const [currentFilters] = useState({ timeRange: '5m' });

  // Fetch agent details
  const fetchAgentDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agentsAPI.getAll();
      if (response.success) {
        const foundAgent = response.data.agents?.find(a => a.agent_id === agentId);
        setAgent(foundAgent);
      }
    } catch (error) {
      antMessage.error('Gagal mengambil data agent');
      console.error('Error fetching agent:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, antMessage]);

  // Fetch monitored items
  const fetchMonitoredItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const response = await agentsAPI.getMonitoredItems(agentId);
      if (response.success) {
        setMonitoredItems(response.data || []);
      }
    } catch (error) {
      antMessage.error('Gagal mengambil monitored items');
      console.error('Error fetching monitored items:', error);
    } finally {
      setItemsLoading(false);
    }
  }, [agentId, antMessage]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const response = await agentsAPI.getLogs(agentId);
      if (response.success) {
        setLogs(response.data || []);
      }
    } catch (error) {
      antMessage.error('Gagal mengambil agent logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [agentId, antMessage]);

  // Fetch Process List
  const fetchProcessList = useCallback(async () => {
    setProcessListLoading(true);
    try {
      const response = await agentsAPI.getProcessList(agentId, { sort_by: processSortBy, limit: 200 });
      if (response.success) {
        setProcessList(response.data || []);
      }
    } catch (error) {
      setProcessList([]);
    } finally {
      setProcessListLoading(false);
    }
  }, [agentId, processSortBy]);

  // Fetch PM2 processes
  const fetchPM2Processes = useCallback(async () => {
    setPm2Loading(true);
    try {
      const response = await agentsAPI.getPM2List(agentId);
      if (response.success) {
        setPm2Processes(response.data || []);
      } else {
        setPm2Processes([]);
        if (response.error) antMessage.error(response.error);
      }
    } catch (error) {
      setPm2Processes([]);
      antMessage.error(error?.response?.data?.message || 'Gagal mengambil data PM2');
    } finally {
      setPm2Loading(false);
    }
  }, [agentId, antMessage]);

  // Fetch Hardware Health
  const fetchHardwareData = useCallback(async () => {
    setHardwareLoading(true);
    try {
      const response = await agentsAPI.getHardwareHealth(agentId);
      if (response.success) {
        setHardwareData(response.data || null);
      }
    } catch (error) {
      setHardwareData(null);
    } finally {
      setHardwareLoading(false);
    }
  }, [agentId]);

  // Fetch Event Viewer logs
  const fetchEventLogs = useCallback(async () => {
    setEventLogsLoading(true);
    try {
      const response = await agentsAPI.getEventLogs(agentId, {
        log_name: evLogName,
        level: evLevel,
        hours: evHours,
        max_events: evMaxEvents,
        keyword: evKeyword || undefined
      });
      if (response.success) {
        setEventLogs(response.events || []);
      } else {
        setEventLogs([]);
        if (response.error) antMessage.error(response.error);
      }
    } catch (error) {
      setEventLogs([]);
      antMessage.error(error?.response?.data?.message || 'Gagal mengambil Event Viewer logs');
    } finally {
      setEventLogsLoading(false);
    }
  }, [agentId, evLogName, evLevel, evHours, evMaxEvents, evKeyword, antMessage]);

  // PM2 action handler
  const handlePM2Action = async (processName, action) => {
    try {
      antMessage.loading(`Mengirim ${action} command...`, 0);
      const response = await agentsAPI.pm2Action(agentId, { action, process_name: processName });
      antMessage.destroy();
      if (response.success) {
        antMessage.success(`PM2 ${action} berhasil untuk ${processName}`);
        setTimeout(fetchPM2Processes, 2000);
      } else {
        antMessage.error(response.error || `PM2 ${action} gagal`);
      }
    } catch (error) {
      antMessage.destroy();
      antMessage.error(`Gagal: ${error?.response?.data?.message || error.message}`);
    }
  };

  useEffect(() => {
    fetchAgentDetails();
    fetchMonitoredItems();
  }, [fetchAgentDetails, fetchMonitoredItems]);

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
    if (activeTab === 'pm2') fetchPM2Processes();
    if (activeTab === 'processes') fetchProcessList();
    if (activeTab === 'hardware') fetchHardwareData();
    if (activeTab === 'eventviewer') fetchEventLogs();
  }, [activeTab, fetchLogs, fetchPM2Processes, fetchProcessList, fetchHardwareData, fetchEventLogs]);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleStatusChange = (data) => {
      if (data.agent_id === agentId) {
        setAgent(prev => prev ? { ...prev, online: data.online, last_seen: data.timestamp } : prev);
      }
    };

    const handleHardwareUpdate = (data) => {
      if (data.agent_id === agentId) {
        // WebSocket event structure: { agent_id, success, data: { disks, network, ..., summary } }
        // Normalize to match REST response structure: { success, data: { ..., summary } }
        const normalized = data.data
          ? { success: data.success, data: data.data }
          : data;
        setHardwareData(normalized);
      }
    };

    socket.on('agent-status-change', handleStatusChange);
    socket.on('agent-hardware-update', handleHardwareUpdate);
    socket.on('agent-updated', (data) => {
      if (data.agent.id === parseInt(agentId) || data.agent.agent_id === agentId) {
        setAgent(prev => ({ ...prev, ...data.agent }));
      }
    });
    return () => {
      socket.off('agent-status-change', handleStatusChange);
      socket.off('agent-hardware-update', handleHardwareUpdate);
      socket.off('agent-updated');
    };
  }, [agentId]);

  // Go back to agents list
  const goBack = () => {
    navigate('/agents');
  };

  // Open create item modal
  const openCreateItemModal = () => {
    setItemModalMode('create');
    setEditingItem(null);
    form.resetFields();
    setItemModalVisible(true);
  };

  // Open edit item modal
  const openEditItemModal = (item) => {
    setItemModalMode('edit');
    setEditingItem(item);
    form.setFieldsValue({
      item_type: item.item_type,
      item_name: item.item_name,
      item_options: item.item_options || {},
      is_active: item.is_active,
      check_interval: item.check_interval
    });
    setItemModalVisible(true);
  };

  // Handle item form submission
  const handleItemSubmit = async (values) => {
    try {
      const itemData = {
        ...values,
        item_options: values.item_options || {}
      };

      if (itemModalMode === 'create') {
        const response = await agentsAPI.createMonitoredItem(agentId, itemData);
        if (response.success) {
          antMessage.success('Monitored item berhasil ditambahkan');
          setItemModalVisible(false);
          fetchMonitoredItems();
        }
      } else {
        const response = await agentsAPI.updateMonitoredItem(agentId, editingItem.id, itemData);
        if (response.success) {
          antMessage.success('Monitored item berhasil diperbarui');
          setItemModalVisible(false);
          fetchMonitoredItems();
        }
      }
    } catch (error) {
      antMessage.error('Gagal menyimpan monitored item');
      console.error('Error saving monitored item:', error);
    }
  };

  // Delete monitored item
  const handleDeleteItem = async (item) => {
    try {
      const response = await agentsAPI.deleteMonitoredItem(agentId, item.id);
      if (response.success) {
        antMessage.success('Monitored item berhasil dihapus');
        fetchMonitoredItems();
      }
    } catch (error) {
      antMessage.error('Gagal menghapus monitored item');
      console.error('Error deleting item:', error);
    }
  };

  // Toggle item active status
  const handleToggleItemActive = async (item) => {
    try {
      const response = await agentsAPI.updateMonitoredItem(agentId, item.id, {
        is_active: !item.is_active
      });
      if (response.success) {
        antMessage.success('Item status berhasil diupdate');
        fetchMonitoredItems();
      }
    } catch (error) {
      antMessage.error('Gagal mengupdate status item');
      console.error('Error toggling item:', error);
    }
  };

  // Send command to agent
  const openCommandModal = (item) => {
    setSelectedItemForCommand(item);
    commandForm.resetFields();
    setCommandModalVisible(true);
  };

  const handleSendCommand = async (values) => {
    try {
      const response = await agentsAPI.sendCommand(agentId, {
        command_type: 'execute-command',
        data: {
          command: values.command,
          timeout: (values.timeout || 30) * 1000
        }
      });
      if (response.success) {
        antMessage.success('Command berhasil dikirim');
        setCommandModalVisible(false);
      }
    } catch (error) {
      antMessage.error('Gagal mengirim command');
      console.error('Error sending command:', error);
    }
  };

  // Restart service
  const handleRestartService = async (item) => {
    try {
      const response = await agentsAPI.sendCommand(agentId, {
        command_type: 'restart-service',
        data: {
          service_name: item.item_name,
          type: item.item_type === 'linux_service' ? 'linux' : item.item_type === 'windows_service' ? 'windows' : 'auto'
        }
      });
      if (response.success) {
        antMessage.success('Restart service command dikirim');
      }
    } catch (error) {
      antMessage.error('Gagal mengirim command restart');
      console.error('Error sending restart command:', error);
    }
  };

  // Handle view target stats (for target type items)
  const handleViewTargetStats = async (item) => {
    const targetId = item.item_options?.target_id;
    if (!targetId) {
      antMessage.error('Target ID tidak ditemukan');
      return;
    }

    setSelectedTargetForStats(item);
    setTargetStatsVisible(true);
    setTargetStatsLoading(true);
    setTargetLogsLoading(true);

    try {
      const logsResp = await targetsAPI.getTargetLogs(targetId, { limit: 100, timeRange: '1h' });
      if (logsResp.success) {
        setTargetLogs(logsResp.data || []);
      }
    } catch (error) {
      console.error('Error fetching target stats:', error);
      antMessage.error('Gagal mengambil statistik target');
    } finally {
      setTargetStatsLoading(false);
      setTargetLogsLoading(false);
    }
  };

  // Get item type icon and color
  const getItemTypeInfo = (type) => {
    switch (type) {
      case 'process':
        return { icon: <PlayCircleOutlined />, color: 'blue', label: 'Process' };
      case 'windows_service':
        return { icon: <FundOutlined />, color: 'purple', label: 'Windows Service' };
      case 'linux_service':
        return { icon: <FundOutlined />, color: 'green', label: 'Linux Service' };
      case 'service':
        return { icon: <DatabaseOutlined />, color: 'cyan', label: 'Service' };
      case 'resources':
        return { icon: <ThunderboltOutlined />, color: 'orange', label: 'Resources' };
      case 'pm2':
        return { icon: <FundOutlined />, color: 'geekblue', label: 'PM2 Process' };
      case 'target':
        return { icon: <DesktopOutlined />, color: 'purple', label: 'Target (IP)' };
      case 'hardware':
        return { icon: <DashboardOutlined />, color: 'magenta', label: 'Hardware' };
      case 'event_viewer':
        return { icon: <FileTextOutlined />, color: 'gold', label: 'Event Viewer' };
      default:
        return { icon: <DesktopOutlined />, color: 'default', label: type };
    }
  };

  // Table columns for monitored items
  const itemColumns = [
    {
      title: 'Name',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text, record) => (
        <Space>
          <span style={{ color: getItemTypeInfo(record.item_type).color }}>
            {getItemTypeInfo(record.item_type).icon}
          </span>
          <span>{text}</span>
          {!record.is_active && <Tag color="red" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'item_type',
      key: 'item_type',
      render: (type) => {
        const info = getItemTypeInfo(type);
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: 'Check Interval',
      dataIndex: 'check_interval',
      key: 'check_interval',
      width: 100,
      render: (val) => `${val}s`,
    },
    {
      title: 'Status',
      dataIndex: 'last_status',
      key: 'last_status',
      width: 100,
      render: (status, record) => {
        const s = status ? status.toLowerCase() : 'unknown';
        const colorMap = {
          'running': 'green',
          'stopped': 'red',
          'active': 'green',
          'inactive': 'red',
          'errored': 'volcano',
          'starting': 'orange',
          'stopping': 'orange',
          'not-found': 'volcano',
          'unknown': 'default',
          'down': 'red',
          'timeout': 'orange',
          'up': 'green'
        };
        const statusTag = (
          <Tag color={colorMap[s] || 'default'}>
            {status || 'NOT CHECKED'}
          </Tag>
        );
        const tooltipContent = (
          <div>
            <div><strong>Message:</strong> {record.last_message || record.last_error || 'No message'}</div>
            {record.last_check && (
              <>
                <div><strong>Time (Local):</strong> {new Date(record.last_check).toLocaleString('id-ID')}</div>
                <div><strong>Time (UTC):</strong> {record.last_check}</div>
              </>
            )}
          </div>
        );
        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            {statusTag}
          </Tooltip>
        );
      },
    },
    {
      title: 'Latency',
      dataIndex: 'last_latency',
      key: 'last_latency',
      width: 90,
      render: (latency) => latency ? `${latency}ms` : '-',
    },
    {
      title: 'Last Check',
      dataIndex: 'last_check',
      key: 'last_check',
      width: 160,
      render: (timestamp) => {
        if (!timestamp) return '-';
        return (
          <Tooltip title={`UTC: ${timestamp}`}>
            {new Date(timestamp).toLocaleString('id-ID')}
          </Tooltip>
        );
      },
    },
    {
      title: 'Error Message',
      dataIndex: 'last_error',
      key: 'last_error',
      width: 200,
      render: (error, record) => {
        if (record.last_status === 'UP' || record.last_status === 'running' || !record.last_status) {
          return '-';
        }
        return error || record.last_message || 'No error details';
      },
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => (
        <Tag color={active ? 'blue' : 'default'}>
          {active ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Toggle Active">
            <Button
              type="text"
              icon={record.is_active ? <StopOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleItemActive(record)}
            />
          </Tooltip>
          {record.item_type.includes('service') && (
            <Tooltip title="Restart Service">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => handleRestartService(record)}
              />
            </Tooltip>
          )}
          {record.item_type === 'target' && (
            <Tooltip title="View Stats">
              <Button
                type="text"
                icon={<LineChartOutlined />}
                onClick={() => handleViewTargetStats(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Send Command">
            <Button
              type="text"
              icon={<DesktopOutlined />}
              onClick={() => openCommandModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEditItemModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Hapus monitored item ini?"
            description={`Anda yakin ingin menghapus "${record.item_name}"?`}
            onConfirm={() => handleDeleteItem(record)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Logs table columns
  const logColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => new Date(ts).toLocaleString('id-ID')
    },
    {
      title: 'Type',
      dataIndex: 'log_type',
      key: 'log_type',
      render: (type) => {
        const colorMap = {
          status: 'blue',
          error: 'red',
          command: 'orange',
          system: 'green'
        };
        return <Tag color={colorMap[type]}>{type?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Item Type',
      dataIndex: 'item_type',
      key: 'item_type',
      render: (type) => type || '-'
    },
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      render: (name) => name || '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (!status) return '-';
        const colorMap = {
          'running': 'green',
          'stopped': 'red',
          'active': 'green',
          'inactive': 'red',
          'error': 'red',
          'success': 'green'
        };
        return <Tag color={colorMap[status]}>{status?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details) => {
        if (!details || Object.keys(details).length === 0) return '-';
        return (
          <Tooltip title={JSON.stringify(details, null, 2)}>
            <Tag>View</Tag>
          </Tooltip>
        );
      }
    }
  ];

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: embedded ? 200 : '100vh' }}>Loading...</div>;
  }

  const contentBody = (
    <>
      {/* Agent Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space align="center">
          <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
            Back to Agents
          </Button>
          <h2 style={{ margin: 0, marginLeft: 8 }}>{agent?.agent_name || 'Agent Detail'}</h2>
          <Tag color={agent?.online ? 'green' : 'red'}>
            {agent?.online ? 'Online' : 'Offline'}
          </Tag>
          <Tag color={agent?.platform === 'windows' ? 'blue' : 'green'}>
            {agent?.platform?.toUpperCase()}
          </Tag>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={fetchAgentDetails}>
          Refresh
        </Button>
      </div>

      {/* Agent Info */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title="Agent Information" size="small">
            <p><strong>Agent ID:</strong> {agent?.agent_id || '-'}</p>
            <p><strong>Description:</strong> {agent?.agent_description || '-'}</p>
            <p><strong>Type:</strong> {agent?.agent_type || '-'}</p>
            <p><strong>Version:</strong> {agent?.agent_version || '-'}</p>
            <p><strong>Last Seen:</strong> {agent?.last_seen ? new Date(agent.last_seen).toLocaleString('id-ID') : '-'}</p>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="System Information" size="small">
            <p><strong>Hostname:</strong> {agent?.hostname || '-'}</p>
            <p><strong>Platform:</strong> {agent?.platform || '-'}</p>
            <p><strong>Architecture:</strong> {agent?.arch || '-'}</p>
            <p><strong>OS Version:</strong> {agent?.os_version || '-'}</p>
          </Card>
        </Col>
      </Row>

      {/* Monitored Items & Logs Tabs */}
      <Card
        activeTabKey={activeTab}
        onTabChange={setActiveTab}
        tabList={[
          { key: 'items', tab: 'Monitored Items' },
          { key: 'resources', tab: 'Resources' },
          { key: 'hardware', tab: 'Hardware' },
          { key: 'processes', tab: 'Processes' },
          { key: 'pm2', tab: 'PM2' },
          ...(agent?.platform === 'windows' ? [{ key: 'eventviewer', tab: 'Event Viewer' }] : []),
          { key: 'logs', tab: 'Logs' },
        ]}
        tabBarExtraContent={
          activeTab === 'items' && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateItemModal}>
              Add Monitored Item
            </Button>
          )
        }
      >
        {activeTab === 'items' && (
          <Table
            columns={itemColumns}
            dataSource={monitoredItems}
            rowKey="id"
            loading={itemsLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true
            }}
          />
        )}

        {activeTab === 'resources' && (
          <>
            <AgentResourceStats agentId={agentId} />
            <div style={{ marginTop: 16 }}>
              <AgentResourceChart agentId={agentId} />
            </div>
          </>
        )}

        {activeTab === 'hardware' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 600 }}>Hardware Health</span>
              <Button icon={<ReloadOutlined />} onClick={fetchHardwareData} loading={hardwareLoading}>Refresh</Button>
            </div>
            {hardwareLoading && !hardwareData ? (
              <div style={{ textAlign: 'center', padding: 40 }}>Loading hardware data...</div>
            ) : hardwareData ? (
              <div>
                {hardwareSummary && (
                  <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: hardwareSummary.overall === 'Critical' ? '#fff1f0' : hardwareSummary.overall === 'Warning' ? '#fff7e6' : '#f6ffed', border: `1px solid ${hardwareSummary.overall === 'Critical' ? '#ffa39e' : hardwareSummary.overall === 'Warning' ? '#ffd591' : '#b7eb8f'}` }}>
                    <strong>Overall Status: </strong>
                    <Tag color={hardwareSummary.overall === 'Critical' ? 'red' : hardwareSummary.overall === 'Warning' ? 'orange' : 'green'}>{hardwareSummary.overall}</Tag>
                    {hardwareSummary.issues?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        {hardwareSummary.issues.map((issue, i) => (
                          <div key={i} style={{ fontSize: 13, color: issue.severity === 'critical' ? '#ff4d4f' : '#fa8c16' }}>
                            <WarningOutlined /> {issue.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                  {/* Disk Health */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#333' }}><DatabaseOutlined /> Disk Health</div>
                      {hardwareData.disks?.length > 0 ? hardwareData.disks.map((d, i) => (
                        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < hardwareData.disks.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{d.name}</div>
                          <Row gutter={[6, 3]}>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Health:</span> <Tag color={d.health === 'Healthy' || d.health === 'OK' ? 'green' : d.health === 'Unknown' ? (d.smart_available ? 'default' : 'orange') : 'red'} style={{ margin: 0, fontSize: 10 }}>{d.health === 'Unknown' && !d.smart_available ? 'N/A' : d.health}</Tag></Col>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Type:</span> <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{d.type}</Tag></Col>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Size:</span> <span style={{ fontSize: 11 }}>{d.size_gb} GB</span></Col>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Bus:</span> <span style={{ fontSize: 11 }}>{d.bus}</span></Col>
                            {d.temperature_celsius > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Temp:</span> <span style={{ fontSize: 11, color: d.temperature_celsius > 50 ? '#ff4d4f' : '#52c41a' }}>{d.temperature_celsius}°C</span></Col>}
                            {d.power_cycles > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Cycles:</span> <span style={{ fontSize: 11 }}>{d.power_cycles}</span></Col>}
                            {d.serial && <Col span={24}><span style={{ fontSize: 11, color: '#888' }}>Serial:</span> <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#666' }}>{d.serial}</span></Col>}
                          </Row>
                        </div>
                      )) : <span style={{ color: '#999', fontSize: 12 }}>No disk data available</span>}
                    </Card>
                  </div>

                  {/* Network */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#333' }}><CloudServerOutlined /> Network</div>
                      {hardwareData.network?.length > 0 ? hardwareData.network.map((n, i) => (
                        <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < hardwareData.network.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{n.name}</div>
                          <Row gutter={[6, 3]}>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Status:</span> <Tag color={isNetworkUp(n.status) ? 'green' : isNetworkDown(n.status) ? 'red' : 'orange'} style={{ margin: 0, fontSize: 10 }}>{n.status}</Tag></Col>
                            {n.speed && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Speed:</span> <span style={{ fontSize: 11 }}>{n.speed}</span></Col>}
                            {n.mac && <Col span={24}><span style={{ fontSize: 11, color: '#888' }}>MAC:</span> <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#666' }}>{n.mac}</span></Col>}
                            {n.ip4 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>IPv4:</span> <span style={{ fontSize: 11 }}>{n.ip4}</span></Col>}
                            {n.ip6 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>IPv6:</span> <span style={{ fontSize: 10, fontFamily: 'monospace' }}>{n.ip6?.substring(0, 20)}...</span></Col>}
                            {n.dhcp && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>DHCP:</span> <span style={{ fontSize: 11 }}>{n.dhcp}</span></Col>}
                          </Row>
                        </div>
                      )) : <span style={{ color: '#999', fontSize: 12 }}>No network data available</span>}
                    </Card>
                  </div>

                  {/* Battery */}
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#333' }}>Battery</div>
                      {hardwareData.battery ? (
                        <div>
                          <div style={{ fontSize: 32, fontWeight: 700, color: hardwareData.battery.charge_percent > 20 ? '#52c41a' : '#ff4d4f', lineHeight: 1.2 }}>
                            {hardwareData.battery.charge_percent}%
                          </div>
                          <Row gutter={[6, 3]} style={{ marginTop: 10 }}>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Health:</span> <Tag color={hardwareData.battery.health === 'OK' ? 'green' : hardwareData.battery.health === 'Low' ? 'orange' : 'red'} style={{ margin: 0, fontSize: 10 }}>{hardwareData.battery.health}</Tag></Col>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Status:</span> <span style={{ fontSize: 11 }}>{hardwareData.battery.status}</span></Col>
                            {hardwareData.battery.estimated_runtime_min && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Runtime:</span> <span style={{ fontSize: 11 }}>{hardwareData.battery.estimated_runtime_min} min</span></Col>}
                            {hardwareData.battery.voltage_mv > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Voltage:</span> <span style={{ fontSize: 11 }}>{(hardwareData.battery.voltage_mv / 1000).toFixed(1)}V</span></Col>}
                            {hardwareData.battery.temperature_celsius > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Temp:</span> <span style={{ fontSize: 11, color: hardwareData.battery.temperature_celsius > 45 ? '#ff4d4f' : '#52c41a' }}>{hardwareData.battery.temperature_celsius}°C</span></Col>}
                            {hardwareData.battery.is_charging !== undefined && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Charging:</span> <Tag color={hardwareData.battery.is_charging ? 'green' : 'default'} style={{ margin: 0, fontSize: 10 }}>{hardwareData.battery.is_charging ? 'Yes' : 'No'}</Tag></Col>}
                          </Row>
                        </div>
                      ) : <span style={{ color: '#999', fontSize: 12 }}>No battery detected</span>}
                    </Card>
                  </div>

                  {/* GPU */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#333' }}>GPU</div>
                      {hardwareData.gpu?.length > 0 ? hardwareData.gpu.map((g, i) => (
                        <div key={i} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{g.name}</div>
                          <Row gutter={[6, 3]}>
                            <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Status:</span> <Tag color={g.status === 'OK' ? 'green' : 'red'} style={{ margin: 0, fontSize: 10 }}>{g.status}</Tag></Col>
                            {g.vram_mb > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>VRAM:</span> <span style={{ fontSize: 11 }}>{g.vram_mb >= 1024 ? `${(g.vram_mb / 1024).toFixed(1)} GB` : `${g.vram_mb} MB`}</span></Col>}
                            {g.driver && <Col span={24}><span style={{ fontSize: 11, color: '#888' }}>Driver:</span> <span style={{ fontSize: 10, color: '#666' }}>{g.driver}</span></Col>}
                            {g.temperature_celsius && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Temp:</span> <span style={{ fontSize: 11, color: g.temperature_celsius > 80 ? '#ff4d4f' : '#52c41a' }}>{g.temperature_celsius}°C</span></Col>}
                            {g.utilization_percent > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Util:</span> <span style={{ fontSize: 11 }}>{g.utilization_percent}%</span></Col>}
                            {g.core_clock_mhz > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Clock:</span> <span style={{ fontSize: 11 }}>{g.core_clock_mhz} MHz</span></Col>}
                            {g.memory_clock_mhz > 0 && <Col span={12}><span style={{ fontSize: 11, color: '#888' }}>Mem Clock:</span> <span style={{ fontSize: 11 }}>{g.memory_clock_mhz} MHz</span></Col>}
                          </Row>
                        </div>
                      )) : <span style={{ color: '#999', fontSize: 12 }}>No GPU detected</span>}
                    </Card>
                  </div>

                  {/* System Info */}
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13, color: '#333' }}>System Info</div>
                      {hardwareData.motherboard ? (
                        <div style={{ fontSize: 11 }}>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>Motherboard</div>
                          <div style={{ color: '#555', marginBottom: 4 }}>{hardwareData.motherboard.manufacturer} {hardwareData.motherboard.product}</div>
                          {hardwareData.motherboard.serial && <div style={{ color: '#888', fontSize: 10, marginBottom: 6 }}>S/N: {hardwareData.motherboard.serial}</div>}
                          {hardwareData.bios && (
                            <>
                              <div style={{ fontWeight: 500, marginTop: 8, marginBottom: 4 }}>BIOS</div>
                              <div style={{ color: '#555', marginBottom: 2 }}>{hardwareData.bios.manufacturer} {hardwareData.bios.version}</div>
                              {hardwareData.bios.release_date && <div style={{ color: '#888', fontSize: 10, marginBottom: 6 }}>Released: {hardwareData.bios.release_date}</div>}
                            </>
                          )}
                          {hardwareData.chassis && (
                            <>
                              <div style={{ fontWeight: 500, marginTop: 8, marginBottom: 4 }}>Chassis</div>
                              <div style={{ color: '#555' }}>{hardwareData.chassis.manufacturer} {hardwareData.chassis.type}</div>
                              {hardwareData.chassis.serial && <div style={{ color: '#888', fontSize: 10 }}>S/N: {hardwareData.chassis.serial}</div>}
                            </>
                          )}
                        </div>
                      ) : hardwareData.bios ? (
                        <div style={{ fontSize: 11 }}>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>BIOS</div>
                          <div style={{ color: '#555', marginBottom: 2 }}>{hardwareData.bios.manufacturer} {hardwareData.bios.version}</div>
                          {hardwareData.bios.release_date && <div style={{ color: '#888', fontSize: 10, marginBottom: 6 }}>Released: {hardwareData.bios.release_date}</div>}
                          {hardwareData.bios.serial && <div style={{ color: '#555', marginBottom: 2 }}>S/N: {hardwareData.bios.serial}</div>}
                        </div>
                      ) : <span style={{ color: '#999', fontSize: 12 }}>No system info available</span>}
                    </Card>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No hardware data available. Agent may be offline.</div>
            )}
          </div>
        )}

        {activeTab === 'processes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <Space>
                <DesktopOutlined /> <strong>Running Processes</strong>
                <Tag>{processList.length} processes</Tag>
              </Space>
              <Space>
                <Input
                  placeholder="Search process..."
                  prefix={<SearchOutlined />}
                  value={processSearch}
                  onChange={(e) => setProcessSearch(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <Select value={processSortBy} onChange={(v) => { setProcessSortBy(v); }} style={{ width: 140 }}>
                  <Select.Option value="memory">Sort: Memory</Select.Option>
                  <Select.Option value="cpu">Sort: CPU</Select.Option>
                </Select>
                <Button icon={<ReloadOutlined />} onClick={fetchProcessList} loading={processListLoading}>
                  Refresh
                </Button>
              </Space>
            </div>
            {!agent?.online ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                Agent offline. Hubungkan agent untuk melihat proses.
              </div>
            ) : (
              <Table
                dataSource={processSearch ? processList.filter(p => p.name?.toLowerCase().includes(processSearch.toLowerCase())) : processList}
                rowKey={(r) => `${r.pid}-${r.name}`}
                loading={processListLoading}
                size="small"
                pagination={{ pageSize: 30, showSizeChanger: true, showTotal: (t) => `${t} processes` }}
                columns={[
                  { title: 'PID', dataIndex: 'pid', key: 'pid', width: 80, sorter: (a, b) => a.pid - b.pid },
                  {
                    title: 'Name', dataIndex: 'name', key: 'name',
                    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                    render: (n) => <strong style={{ fontSize: 12 }}>{n}</strong>
                  },
                  {
                    title: 'CPU %', dataIndex: 'cpu', key: 'cpu', width: 80,
                    sorter: (a, b) => a.cpu - b.cpu,
                    defaultSortOrder: 'descend',
                    render: (v) => {
                      const val = Number(v) || 0;
                      return <span style={{ color: val > 50 ? '#ff4d4f' : val > 20 ? '#fa8c16' : val > 0 ? '#52c41a' : '#999', fontWeight: val > 0 ? 600 : 400 }}>{val}%</span>;
                    }
                  },
                  {
                    title: 'Memory', dataIndex: 'memory_mb', key: 'memory_mb', width: 110,
                    sorter: (a, b) => a.memory_mb - b.memory_mb,
                    render: (v) => {
                      const val = Number(v) || 0;
                      const display = val >= 1024 ? `${(val / 1024).toFixed(1)} GB` : `${val.toFixed(1)} MB`;
                      return <span style={{ color: val > 500 ? '#ff4d4f' : val > 100 ? '#fa8c16' : 'inherit' }}>{display}</span>;
                    }
                  },
                  {
                    title: 'Mem %', dataIndex: 'memory_percent', key: 'memory_percent', width: 80,
                    sorter: (a, b) => a.memory_percent - b.memory_percent,
                    render: (v) => `${(Number(v) || 0).toFixed(1)}%`
                  },
                  {
                    title: 'Threads', dataIndex: 'threads', key: 'threads', width: 80,
                    sorter: (a, b) => (a.threads || 0) - (b.threads || 0),
                    render: (v) => v || '-'
                  }
                ]}
              />
            )}
          </div>
        )}

        {activeTab === 'pm2' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <Space>
                <CloudServerOutlined /> <strong>PM2 Process Manager</strong>
              </Space>
              <Button icon={<ReloadOutlined />} onClick={fetchPM2Processes} loading={pm2Loading}>
                Refresh
              </Button>
            </div>
            {!agent?.online ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                Agent offline. Hubungkan agent untuk melihat PM2 processes.
              </div>
            ) : (
              <Table
                dataSource={pm2Processes}
                rowKey={(r) => `${r.pm_id}-${r.name}`}
                loading={pm2Loading}
                pagination={false}
                columns={[
                  {
                    title: 'ID', dataIndex: 'pm_id', key: 'pm_id', width: 60,
                    render: (id) => <Tag>{id}</Tag>
                  },
                  { title: 'Name', dataIndex: 'name', key: 'name', render: (n) => <strong>{n}</strong> },
                  {
                    title: 'Status', dataIndex: 'status', key: 'status',
                    render: (s) => {
                      const c = { running: 'green', stopped: 'red', errored: 'volcano', stopping: 'orange', starting: 'blue' };
                      return <Tag color={c[s] || 'default'}>{s?.toUpperCase()}</Tag>;
                    }
                  },
                  { title: 'PID', dataIndex: 'pid', key: 'pid', render: (p) => p > 0 ? p : '-' },
                  { title: 'CPU %', dataIndex: 'cpu', key: 'cpu', render: (v) => `${v || 0}%` },
                  { title: 'Memory', dataIndex: 'memory_mb', key: 'memory_mb', render: (v) => `${v || 0} MB` },
                  {
                    title: 'Uptime', dataIndex: 'uptime', key: 'uptime',
                    render: (ts) => {
                      if (!ts) return '-';
                      const ms = Date.now() - ts;
                      const h = Math.floor(ms / 3600000);
                      const m = Math.floor((ms % 3600000) / 60000);
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    }
                  },
                  { title: 'Restarts', dataIndex: 'restarts', key: 'restarts', render: (v) => <Tag color={v > 5 ? 'volcano' : 'default'}>{v || 0}</Tag> },
                  { title: 'Mode', dataIndex: 'exec_mode', key: 'exec_mode', render: (m) => <Tag>{m}</Tag> },
                  {
                    title: 'Actions', key: 'actions',
                    render: (_, record) => (
                      <Space size="small">
                        <Tooltip title="Restart">
                          <Button type="text" icon={<ReloadOutlined />} onClick={() => handlePM2Action(record.name, 'restart')} />
                        </Tooltip>
                        {record.status === 'running' ? (
                          <Tooltip title="Stop">
                            <Button type="text" danger icon={<StopOutlined />} onClick={() => handlePM2Action(record.name, 'stop')} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Start">
                            <Button type="text" style={{ color: '#52c41a' }} icon={<PlayCircleOutlined />} onClick={() => handlePM2Action(record.name, 'start')} />
                          </Tooltip>
                        )}
                      </Space>
                    )
                  }
                ]}
              />
            )}
          </div>
        )}

        {activeTab === 'eventviewer' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <Space wrap>
                <FileTextOutlined /> <strong>Windows Event Viewer</strong>
              </Space>
              <Space wrap>
                <Select value={evLogName} onChange={setEvLogName} style={{ width: 150 }}>
                  <Select.Option value="Application">Application</Select.Option>
                  <Select.Option value="System">System</Select.Option>
                  <Select.Option value="Security">Security</Select.Option>
                  <Select.Option value="Setup">Setup</Select.Option>
                </Select>
                <Select value={evLevel} onChange={setEvLevel} allowClear placeholder="All Levels" style={{ width: 130 }}>
                  <Select.Option value="critical">Critical</Select.Option>
                  <Select.Option value="error">Error</Select.Option>
                  <Select.Option value="warning">Warning</Select.Option>
                  <Select.Option value="information">Information</Select.Option>
                </Select>
                <Select value={evHours} onChange={setEvHours} style={{ width: 130 }}>
                  <Select.Option value={1}>Last 1 hour</Select.Option>
                  <Select.Option value={6}>Last 6 hours</Select.Option>
                  <Select.Option value={24}>Last 24 hours</Select.Option>
                  <Select.Option value={72}>Last 3 days</Select.Option>
                  <Select.Option value={168}>Last 7 days</Select.Option>
                </Select>
                <Input
                  placeholder="Keyword..."
                  prefix={<SearchOutlined />}
                  value={evKeyword}
                  onChange={(e) => setEvKeyword(e.target.value)}
                  onPressEnter={fetchEventLogs}
                  style={{ width: 160 }}
                  allowClear
                />
                <Button type="primary" icon={<FilterOutlined />} onClick={fetchEventLogs} loading={eventLogsLoading}>
                  Query
                </Button>
              </Space>
            </div>
            {!agent?.online ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                Agent offline. Hubungkan agent untuk melihat Event Viewer.
              </div>
            ) : (
              <Table
                dataSource={eventLogs}
                rowKey={(r, i) => `${r.event_id}-${r.time_created}-${i}`}
                loading={eventLogsLoading}
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} events` }}
                size="small"
                columns={[
                  {
                    title: 'Time', dataIndex: 'time_created', key: 'time_created', width: 180,
                    render: (t) => t ? new Date(t).toLocaleString('id-ID') : '-'
                  },
                  {
                    title: 'Level', dataIndex: 'level', key: 'level', width: 110,
                    render: (l) => {
                      const icons = { 'Error': <CloseCircleOutlined />, 'Warning': <WarningOutlined />, 'Information': <InfoCircleOutlined />, 'Critical': <CloseCircleOutlined /> };
                      const colors = { 'Error': 'red', 'Warning': 'orange', 'Information': 'blue', 'Critical': 'volcano' };
                      return <Tag icon={icons[l]} color={colors[l] || 'default'}>{l}</Tag>;
                    }
                  },
                  { title: 'Event ID', dataIndex: 'event_id', key: 'event_id', width: 90 },
                  { title: 'Source', dataIndex: 'provider', key: 'provider', width: 180, ellipsis: true },
                  {
                    title: 'Message', dataIndex: 'message', key: 'message', ellipsis: true,
                    render: (m) => (
                      <Tooltip title={m} placement="topLeft">
                        <span>{m}</span>
                      </Tooltip>
                    )
                  }
                ]}
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, padding: 8, background: '#fafafa', borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
                      {record.message}
                    </div>
                  )
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <Table
            columns={logColumns}
            dataSource={logs}
            rowKey="id"
            loading={logsLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true
            }}
          />
        )}
      </Card>

      {/* Create/Edit Item Modal */}
      <Modal
        title={itemModalMode === 'create' ? 'Add Monitored Item' : 'Edit Monitored Item'}
        open={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleItemSubmit}
          initialValues={{
            check_interval: 30,
            is_active: true
          }}
        >
          <Form.Item
            label="Type"
            name="item_type"
            rules={[{ required: true, message: 'Item type is required' }]}
          >
            <Select placeholder="Select item type">
              <Select.Option value="process">Process</Select.Option>
              <Select.Option value="pm2">PM2 Process</Select.Option>
              <Select.Option value="windows_service">Windows Service</Select.Option>
              <Select.Option value="linux_service">Linux Service</Select.Option>
              <Select.Option value="service">Auto-Detect Service</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Name"
            name="item_name"
            rules={[{ required: true, message: 'Item name is required' }]}
          >
            <Input placeholder="e.g., nginx, apache2, myapp" />
          </Form.Item>

          <Form.Item
            label="Check Interval (seconds)"
            name="check_interval"
            rules={[{ required: true, message: 'Check interval is required' }]}
          >
            <Input type="number" min="5" placeholder="e.g., 30" />
          </Form.Item>

          <Form.Item
            label="Active"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setItemModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {itemModalMode === 'create' ? 'Add' : 'Update'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Send Command Modal */}
      <Modal
        title={`Send Command to ${selectedItemForCommand?.item_name || 'Agent'}`}
        open={commandModalVisible}
        onCancel={() => setCommandModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={commandForm}
          layout="vertical"
          onFinish={handleSendCommand}
          initialValues={{
            timeout: 30
          }}
        >
          <Form.Item
            label="Command"
            name="command"
            rules={[{ required: true, message: 'Command is required' }]}
            help="Enter shell command to execute on the agent"
          >
            <TextArea rows={4} placeholder="e.g., systemctl restart nginx" />
          </Form.Item>

          <Form.Item
            label="Timeout (seconds)"
            name="timeout"
            rules={[{ required: true, message: 'Timeout is required' }]}
          >
            <Input type="number" min="5" max="300" />
          </Form.Item>

          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fff7e6', borderRadius: 4 }}>
            <strong>⚠️ Warning:</strong>
            <p style={{ margin: '8px 0 0 0' }}>
              Executing commands on remote servers can be dangerous. Only execute commands that you trust.
              Commands will be validated against dangerous patterns.
            </p>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCommandModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Send Command
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Target Stats Modal for IP Monitoring Items */}
      <Modal
        title={`Target Statistics - ${selectedTargetForStats?.item_name || 'Target'}`}
        open={targetStatsVisible}
        onCancel={() => setTargetStatsVisible(false)}
        footer={null}
        width={1000}
      >
        {selectedTargetForStats && (
          <div>
            {targetStatsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
            ) : (
              <>
                <TargetStats
                  targetId={selectedTargetForStats?.item_options?.target_id}
                  filters={currentFilters}
                />
                <LatencyChart
                  targetId={selectedTargetForStats?.item_options?.target_id}
                  filters={currentFilters}
                  logs={targetLogs}
                  loading={targetLogsLoading}
                />
                <Card title="Logs" style={{ marginTop: 16 }}>
                  <Table
                    dataSource={targetLogs}
                    rowKey="id"
                    loading={targetLogsLoading}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    columns={[
                      { title: 'Time', dataIndex: 'timestamp', key: 'timestamp', render: (t) => new Date(t).toLocaleString('id-ID') },
                      { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'UP' ? 'green' : 'red'}>{s}</Tag> },
                      { title: 'Latency', dataIndex: 'latency', key: 'latency', render: (l) => l ? `${l}ms` : '-' },
                      { title: 'Message', dataIndex: 'message', key: 'message', ellipsis: true }
                    ]}
                  />
                </Card>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );

  if (embedded) {
    return contentBody;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Content style={{ padding: 24 }}>
        {contentBody}
      </Layout.Content>
    </Layout>
  );
};

export default AgentDetail;
